import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable } from '@dnd-kit/core';
import { useWidgetBuilder } from '../context/WidgetBuilderContext';
import { Edit, Trash2, GripVertical, Settings } from 'lucide-react';

export function SortableWidget({ id, widget, children, onEdit, onDelete }) {
  const { isEditing } = useWidgetBuilder();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id,
    data: { type: 'widget', widget }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={`relative group ${isEditing ? 'mb-2' : ''}`}>
      {children}
      
      {/* Edit Overlay */}
      {isEditing && (
        <div className="absolute inset-0 bg-[var(--tf-primary)]/10 border-2 border-[var(--tf-primary)] rounded-lg opacity-100 flex items-center justify-center gap-2 z-10 transition-opacity backdrop-blur-[1px]">
          <div 
            {...attributes} 
            {...listeners} 
            className="absolute top-2 left-2 p-1.5 bg-black/60 rounded cursor-grab active:cursor-grabbing text-white/70 hover:text-white"
          >
            <GripVertical className="w-4 h-4" />
          </div>
          
          <button 
            onClick={(e) => { e.stopPropagation(); onEdit(widget); }}
            className="bg-[var(--tf-primary)] hover:bg-[var(--tf-primary-hover)] text-white px-3 py-1.5 rounded flex items-center gap-1.5 font-bold text-xs shadow-lg"
          >
            <Edit className="w-3.5 h-3.5" /> Edit
          </button>
          
          <button 
            onClick={(e) => { e.stopPropagation(); onDelete(widget.id); }}
            className="absolute top-2 right-2 p-1.5 bg-black/60 rounded text-red-400 hover:text-red-500 hover:bg-black/80 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export function WidgetDropZone({ id, children }) {
  const { isEditing } = useWidgetBuilder();
  const { isOver, setNodeRef } = useDroppable({
    id,
    data: { type: 'zone', position: id }
  });

  if (!isEditing) {
    return <div className="space-y-4">{children}</div>;
  }

  return (
    <div 
      ref={setNodeRef}
      className={`min-h-[100px] p-2 rounded-lg border-2 border-dashed transition-colors space-y-2 relative
        ${isOver ? 'border-[var(--tf-primary)] bg-[var(--tf-primary)]/5' : 'border-zinc-700 bg-black/20'}
      `}
    >
      <div className="absolute -top-3 left-3 bg-[#0d111a] px-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
        {id} AREA
      </div>
      {children}
      {React.Children.count(children) === 0 && (
        <div className="h-20 flex items-center justify-center text-xs text-zinc-500 tf-mono italic">
          Drop widgets here
        </div>
      )}
    </div>
  );
}
